import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SelectableBox } from '@/components/ui/selectable-box'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Mail, Shapes, Volume2 } from 'lucide-react'
import type { Control } from 'react-hook-form'
import type { FormValues } from './agent-detail.schema'
import type { AgentChannel } from '@super/shared-types'
import type { AgentTone } from './agent-detail.constants'
import { toneOptions } from './agent-detail.constants'
import { VoiceCardContent } from './VoiceCard'
import { EmailCardContent } from './EmailCard'

type ChannelSettingsCardProps = {
  control: Control<FormValues>
  channels: AgentChannel[]
  directionForDisplay: 'INBOUND' | 'OUTBOUND'
}

/** Subtle channel section wrapper — light background with small icon label */
function ChannelSection({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg">
      <div className="flex items-center gap-1.5 mb-4">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-semibold">{label}</span>
      </div>
      {children}
    </div>
  )
}

export function ChannelSettingsCard({
  control,
  channels,
  directionForDisplay,
}: ChannelSettingsCardProps) {
  const hasVoice = channels.includes('CALL')
  const hasEmail = channels.includes('EMAIL')
  const hasBoth = hasVoice && hasEmail
  const isOutboundEmail = directionForDisplay === 'OUTBOUND' && hasEmail

  if (!hasVoice && !hasEmail) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Shapes className="w-4 h-4" />
          Agent style
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={control}
          name="tone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tone</FormLabel>
              <FormControl>
                <div className="flex flex-wrap gap-2">
                  {toneOptions.map(tone => (
                    <SelectableBox
                      key={tone}
                      id={tone}
                      selected={(field.value ?? []).includes(tone)}
                      onSelect={id => {
                        const typedId = id as AgentTone
                        const current = field.value ?? []
                        const newValue = current.includes(typedId)
                          ? current.filter(t => t !== typedId)
                          : [...current, typedId]
                        field.onChange(newValue)
                      }}
                    >
                      <span className="capitalize">{tone}</span>
                    </SelectableBox>
                  ))}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {hasVoice && hasBoth && (
          <ChannelSection icon={Volume2} label="Voice">
            <VoiceCardContent control={control} />
          </ChannelSection>
        )}

        {hasVoice && !hasBoth && <VoiceCardContent control={control} />}

        {isOutboundEmail && hasBoth && (
          <ChannelSection icon={Mail} label="Email">
            <EmailCardContent control={control} />
          </ChannelSection>
        )}

        {isOutboundEmail && !hasBoth && <EmailCardContent control={control} />}
      </CardContent>
    </Card>
  )
}
